import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { getMyTasks } from '../services/taskService';
import './TasksPage.css';

const STATUS_OPTIONS = [
  { label: 'All status', value: '' },
  { label: 'Todo', value: 'todo' },
  { label: 'In progress', value: 'in-progress' },
  { label: 'Done', value: 'done' },
];

const PRIORITY_OPTIONS = [
  { label: 'All priority', value: '' },
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

const DUE_OPTIONS = [
  { label: 'Any due date', value: '' },
  { label: 'Overdue', value: 'overdue' },
  { label: 'Due today', value: 'today' },
  { label: 'Upcoming (7 days)', value: 'upcoming' },
];

const fmtDate = (value) => {
  if (!value) return 'No due date';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'No due date';
  return d.toLocaleDateString('en-CA', { month: 'short', day: 'numeric', year: 'numeric' });
};

const dueLabel = (task) => {
  if (!task.dueDate) return 'none';
  const due = new Date(task.dueDate);
  if (Number.isNaN(due.getTime())) return 'none';

  const now = new Date();
  const start = new Date(now);
  start.setHours(0, 0, 0, 0);

  if (task.status !== 'done' && due < start) return 'overdue';

  const endToday = new Date(start);
  endToday.setDate(endToday.getDate() + 1);
  if (due >= start && due < endToday) return 'today';

  const inWeek = new Date(start);
  inWeek.setDate(inWeek.getDate() + 8);
  if (due >= endToday && due < inWeek) return 'upcoming';

  return 'later';
};

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [dueFilter, setDueFilter] = useState('');

  const loadTasks = () => {
    setLoading(true);
    getMyTasks({ status: statusFilter, priority: priorityFilter, due: dueFilter })
      .then((res) => setTasks(res.data.tasks || []))
      .catch((err) => {
        console.error('Failed to load tasks:', err);
        setTasks([]);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadTasks();
  }, [statusFilter, priorityFilter, dueFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const summary = useMemo(() => {
    const counts = {
      total: tasks.length,
      overdue: 0,
      today: 0,
      upcoming: 0,
      done: 0,
    };

    for (const task of tasks) {
      const label = dueLabel(task);
      if (label === 'overdue') counts.overdue += 1;
      if (label === 'today') counts.today += 1;
      if (label === 'upcoming') counts.upcoming += 1;
      if (task.status === 'done') counts.done += 1;
    }

    return counts;
  }, [tasks]);

  return (
    <div className="tasks-page">
      <div className="tasks-header">
        <div>
          <h1 className="tasks-title">My Tasks</h1>
          <p className="tasks-subtitle">Assigned tasks across all spaces and projects you can access.</p>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={loadTasks}>Refresh</button>
      </div>

      <div className="tasks-summary-grid">
        <div className="tasks-summary-card"><span>Total</span><strong>{summary.total}</strong></div>
        <div className="tasks-summary-card"><span>Overdue</span><strong>{summary.overdue}</strong></div>
        <div className="tasks-summary-card"><span>Due Today</span><strong>{summary.today}</strong></div>
        <div className="tasks-summary-card"><span>Upcoming</span><strong>{summary.upcoming}</strong></div>
      </div>

      <div className="tasks-filters">
        <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {STATUS_OPTIONS.map((option) => <option key={option.label} value={option.value}>{option.label}</option>)}
        </select>
        <select className="form-select" value={priorityFilter} onChange={(e) => setPriorityFilter(e.target.value)}>
          {PRIORITY_OPTIONS.map((option) => <option key={option.label} value={option.value}>{option.label}</option>)}
        </select>
        <select className="form-select" value={dueFilter} onChange={(e) => setDueFilter(e.target.value)}>
          {DUE_OPTIONS.map((option) => <option key={option.label} value={option.value}>{option.label}</option>)}
        </select>
      </div>

      <div className="tasks-list-wrap">
        {loading ? (
          <div className="tasks-empty">Loading tasks...</div>
        ) : tasks.length === 0 ? (
          <div className="tasks-empty">No tasks match your current filters.</div>
        ) : (
          <ul className="tasks-list">
            {tasks.map((task) => (
              <li key={task._id} className={`task-row due-${dueLabel(task)}`}>
                <div className="task-main">
                  <h3>{task.title}</h3>
                  <p>{task.description || 'No description'}</p>
                  <div className="task-links">
                    <Link to={`/spaces/${task.spaceId?._id}`}>{task.spaceId?.name || 'Space'}</Link>
                    {task.projectId?._id && <Link to={`/projects/${task.projectId._id}`}>{task.projectId.name || 'Project'}</Link>}
                  </div>
                </div>
                <div className="task-meta">
                  <span className={`task-pill status-${task.status}`}>{task.status}</span>
                  <span className={`task-pill priority-${task.priority}`}>{task.priority}</span>
                  <span className="task-date">{fmtDate(task.dueDate)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
